import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { audienceApi } from '../../api';

const AudienceMembersModal = ({ show, audience, onUpdateSubscription, onClose }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const fetchMembers = useCallback(async () => {
    if (!audience) return;

    setLoading(true);
    try {
      const data = await audienceApi.getMembers(audience.id, page, pageSize);
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching audience members:', error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [audience, page, pageSize]);

  useEffect(() => {
    if (show && audience) {
      fetchMembers();
    }
  }, [show, audience, fetchMembers]);

  const handleSubscriptionToggle = async (member, currentlySubscribed) => {
    try {
      await onUpdateSubscription(member.id, audience.id, !currentlySubscribed);
      // Refresh members list
      fetchMembers();
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
  };

  const isSubscribed = (member) => {
    const info = member.memberInfo || {};
    if (!info.unsubscribedAudiences || info.unsubscribedAudiences.length === 0) {
      return true;
    }
    return !info.unsubscribedAudiences.includes(audience?.id);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {audience?.name} - Members
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage subscription status for members in this audience
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="py-12 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading members...</p>
          </div>
        ) : members.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <p>No members in this audience.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {members.map((member) => {
              const info = member.memberInfo || {};
              const subscribed = isSubscribed(member);

              return (
                <div
                  key={member.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    subscribed ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        subscribed ? 'bg-purple-100' : 'bg-gray-200'
                      }`}>
                        <span className={`font-medium text-sm ${
                          subscribed ? 'text-purple-600' : 'text-gray-500'
                        }`}>
                          {info.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className={`text-sm font-medium ${subscribed ? 'text-gray-900' : 'text-gray-500'}`}>
                        {info.name || 'Unknown'}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        {info.email && (
                          <span className="flex items-center text-xs text-gray-500">
                            <EnvelopeIcon className="h-3 w-3 mr-1" />
                            {info.email}
                          </span>
                        )}
                        {info.phone && (
                          <span className="flex items-center text-xs text-gray-500">
                            <PhoneIcon className="h-3 w-3 mr-1" />
                            {info.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      subscribed 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {subscribed ? 'Subscribed' : 'Unsubscribed'}
                    </span>
                    <button
                      onClick={() => handleSubscriptionToggle(member, subscribed)}
                      className={`text-sm font-medium px-3 py-1 rounded border ${
                        subscribed
                          ? 'border-red-300 text-red-600 hover:bg-red-50'
                          : 'border-green-300 text-green-600 hover:bg-green-50'
                      }`}
                    >
                      {subscribed ? 'Unsubscribe' : 'Subscribe'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {members.length > 0 && (
          <div className="mt-4 flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-gray-500">
              Page {page}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={members.length < pageSize}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudienceMembersModal;

